import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { api, API } from '../lib/api';
import { Smartphone, KeyRound, Lock, User, Store, ShieldCheck, ArrowLeft } from 'lucide-react';

/**
 * Unified login hub: select role (Customer | Vendor), then enter credentials.
 * Each role supports OTP-login, password-login, and forgot-password flows.
 */
const LoginHub = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { toast } = useToast();

  const initialRole = params.get('as') === 'vendor' ? 'vendor' : 'customer';
  const [role, setRole] = useState(initialRole);
  const [mode, setMode] = useState('home'); // home | otp | forgot | set-pw
  const [authTab, setAuthTab] = useState('otp'); // otp | password

  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [devOtp, setDevOtp] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pendingToken, setPendingToken] = useState(null);

  const validateMobile = () => {
    if (!mobile || mobile.length !== 10) { toast({ title: 'Enter a valid 10-digit mobile number', variant: 'destructive' }); return false; }
    return true;
  };
  const validateNewPassword = () => {
    if (newPassword.length < 8) { toast({ title: 'Password must be at least 8 characters', variant: 'destructive' }); return false; }
    if (!/[A-Za-z]/.test(newPassword) || !/\d/.test(newPassword)) { toast({ title: 'Password must contain letters and numbers', variant: 'destructive' }); return false; }
    if (newPassword !== confirmPassword) { toast({ title: 'Passwords do not match', variant: 'destructive' }); return false; }
    return true;
  };

  const persistSession = (res) => {
    // Auth tokens are now in httpOnly cookies (tgm_customer_jwt / tgm_vendor_jwt)
    // set by the backend on every login response. We only cache non-sensitive
    // profile data here for the UI to hydrate "logged in" state on page load.
    if (role === 'vendor') {
      if (res.vendor) localStorage.setItem('tgmart_vendor', JSON.stringify(res.vendor));
    } else if (res.user) {
      localStorage.setItem('tgmart_user', JSON.stringify(res.user));
    }
    window.dispatchEvent(new Event('tgmart-session-change'));
  };

  const sendOtp = async () => {
    if (!validateMobile()) return;
    try {
      setSubmitting(true);
      const r = await api.sendOtp(mobile);
      setMode('otp');
      if (r.dev_otp) { setDevOtp(r.dev_otp); toast({ title: 'OTP sent (dev)', description: `Dev OTP: ${r.dev_otp}` }); }
      else { toast({ title: 'OTP sent', description: `+91 ${mobile}` }); }
    } catch (err) { toast({ title: 'Failed to send OTP', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' }); }
    finally { setSubmitting(false); }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    if (otp.length < 4) return toast({ title: 'Enter the OTP', variant: 'destructive' });
    try {
      setSubmitting(true);
      let res;
      if (role === 'vendor') {
        const r = await axios.post(`${API}/vendor/auth/login`, { mobile, otp }, { withCredentials: true });
        res = r.data;
      } else {
        res = await api.verifyOtp(mobile, otp);
      }
      persistSession(res);
      // Optional set-password prompt after OTP login
      try {
        const url = role === 'vendor' ? `${API}/vendor/auth/has-password` : `${API}/auth/has-password`;
        const hp = await axios.get(url, { withCredentials: true, headers: { Authorization: `Bearer ${res.token}` } });
        if (!hp.data.has_password) {
          setPendingToken(res.token);
          setMode('set-pw');
          return;
        }
      } catch (err) { console.warn('LoginHub: has-password check failed (non-fatal)', err); }
      toast({ title: 'Welcome back!', description: role === 'vendor' ? 'Vendor session active' : 'Customer session active' });
      navigate(role === 'vendor' ? '/vendor/dashboard' : '/account');
    } catch (err) { toast({ title: 'Login failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' }); }
    finally { setSubmitting(false); }
  };

  const loginWithPassword = async (e) => {
    e?.preventDefault?.();
    if (!validateMobile()) return;
    if (!password) return toast({ title: 'Enter your password', variant: 'destructive' });
    try {
      setSubmitting(true);
      const url = role === 'vendor' ? `${API}/vendor/auth/login-password` : `${API}/auth/login-password`;
      const r = await axios.post(url, { mobile, password }, { withCredentials: true });
      persistSession(r.data);
      toast({ title: 'Welcome back', description: r.data.vendor?.name || r.data.user?.name || '' });
      navigate(role === 'vendor' ? '/vendor/dashboard' : '/account');
    } catch (err) { toast({ title: 'Login failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' }); }
    finally { setSubmitting(false); }
  };

  const startForgot = async () => {
    if (!validateMobile()) return;
    try {
      setSubmitting(true);
      const r = await api.sendOtp(mobile);
      setMode('forgot'); setOtp(''); setNewPassword(''); setConfirmPassword('');
      if (r.dev_otp) { setDevOtp(r.dev_otp); toast({ title: 'OTP sent (dev)', description: `Dev OTP: ${r.dev_otp}` }); }
      else { toast({ title: 'OTP sent', description: `+91 ${mobile}` }); }
    } catch (err) { toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' }); }
    finally { setSubmitting(false); }
  };

  const submitForgot = async (e) => {
    e?.preventDefault?.();
    if (otp.length < 4) return toast({ title: 'Enter the OTP', variant: 'destructive' });
    if (!validateNewPassword()) return;
    try {
      setSubmitting(true);
      const url = role === 'vendor' ? `${API}/vendor/auth/forgot-password/reset` : `${API}/auth/forgot-password/reset`;
      const r = await axios.post(url, { mobile, otp, new_password: newPassword }, { withCredentials: true });
      persistSession(r.data);
      toast({ title: 'Password reset', description: 'You are now signed in.' });
      navigate(role === 'vendor' ? '/vendor/dashboard' : '/account');
    } catch (err) { toast({ title: 'Reset failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' }); }
    finally { setSubmitting(false); }
  };

  const setPwNow = async () => {
    if (!validateNewPassword()) return;
    try {
      setSubmitting(true);
      const url = role === 'vendor' ? `${API}/vendor/auth/set-password` : `${API}/auth/set-password`;
      await axios.post(url, { new_password: newPassword }, { withCredentials: true, headers: { Authorization: `Bearer ${pendingToken}` } });
      toast({ title: 'Password set', description: 'Skip OTP next time!' });
      navigate(role === 'vendor' ? '/vendor/dashboard' : '/account');
    } catch (err) { toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' }); }
    finally { setSubmitting(false); }
  };

  const switchRole = (next) => {
    setRole(next);
    // reset to home state on role change
    setMode('home'); setAuthTab('otp');
    setOtp(''); setPassword(''); setNewPassword(''); setConfirmPassword(''); setDevOtp(null); setPendingToken(null);
  };

  const accentColor = role === 'vendor' ? '#2c54b2' : '#ee5e23';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0b1220]" data-testid="login-hub">
      <Header />
      <div className="max-w-xl mx-auto px-4 py-10">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Sign in to TelanganaMart</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Choose how you want to sign in</p>
        </div>

        {/* Role tabs */}
        <div className="grid grid-cols-2 gap-2 mb-5 bg-white dark:bg-[#111827] border border-gray-200 dark:border-gray-800 rounded-lg p-1.5">
          <button
            onClick={() => switchRole('customer')}
            className={`flex items-center justify-center gap-2 py-3 rounded-md font-semibold text-sm transition ${role === 'customer' ? 'bg-[#ee5e23] text-white shadow' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
            data-testid="role-tab-customer"
          >
            <User className="w-4 h-4" /> Customer Login
          </button>
          <button
            onClick={() => switchRole('vendor')}
            className={`flex items-center justify-center gap-2 py-3 rounded-md font-semibold text-sm transition ${role === 'vendor' ? 'bg-[#2c54b2] text-white shadow' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
            data-testid="role-tab-vendor"
          >
            <Store className="w-4 h-4" /> Vendor Login
          </button>
        </div>

        <div className="bg-white dark:bg-[#111827] border border-gray-200 dark:border-gray-800 rounded-lg p-6 shadow-sm">
          {mode === 'home' && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="hub-mobile" className="text-xs text-gray-700 dark:text-gray-300">Mobile Number</Label>
                <div className="flex gap-2 mt-1">
                  <div className="flex items-center px-3 border border-gray-300 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200">+91</div>
                  <Input id="hub-mobile" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit mobile" data-testid="hub-mobile-input" />
                </div>
              </div>

              <div className="flex border-b border-gray-200 dark:border-gray-700">
                <button type="button" onClick={() => setAuthTab('otp')} className={`flex-1 px-3 py-2 text-sm font-medium border-b-2 -mb-px ${authTab === 'otp' ? `text-[${accentColor}]` : 'border-transparent text-gray-500'}`} style={authTab === 'otp' ? { borderColor: accentColor, color: accentColor } : {}} data-testid="hub-tab-otp">
                  <Smartphone className="w-4 h-4 inline mr-1" /> OTP Login
                </button>
                <button type="button" onClick={() => setAuthTab('password')} className={`flex-1 px-3 py-2 text-sm font-medium border-b-2 -mb-px ${authTab === 'password' ? `text-[${accentColor}]` : 'border-transparent text-gray-500'}`} style={authTab === 'password' ? { borderColor: accentColor, color: accentColor } : {}} data-testid="hub-tab-password">
                  <Lock className="w-4 h-4 inline mr-1" /> Password
                </button>
              </div>

              {authTab === 'otp' ? (
                <Button onClick={sendOtp} disabled={submitting} className="w-full h-11 text-white" style={{ backgroundColor: accentColor }} data-testid="hub-send-otp-btn">
                  <Smartphone className="w-4 h-4 mr-2" /> {submitting ? 'Sending…' : 'Send OTP'}
                </Button>
              ) : (
                <form onSubmit={loginWithPassword} className="space-y-3">
                  <div>
                    <Label htmlFor="hub-pw" className="text-xs text-gray-700 dark:text-gray-300">Password</Label>
                    <Input id="hub-pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" autoComplete="current-password" data-testid="hub-password-input" />
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full h-11 text-white" style={{ backgroundColor: accentColor }} data-testid="hub-password-login-btn">
                    <KeyRound className="w-4 h-4 mr-2" /> {submitting ? 'Signing in…' : 'Sign In'}
                  </Button>
                  <button type="button" onClick={startForgot} className="w-full text-xs text-center hover:underline" style={{ color: accentColor }} data-testid="hub-forgot-link">
                    Forgot password? Reset via OTP
                  </button>
                </form>
              )}

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200 dark:border-gray-700" /></div>
                <div className="relative flex justify-center text-xs uppercase"><span className="bg-white dark:bg-[#111827] px-2 text-gray-500">or</span></div>
              </div>

              <GoogleSignInButton
                contextKey={`login-hub-${role}`}
                onCredential={async (credential) => {
                  try {
                    setSubmitting(true);
                    const url = role === 'vendor' ? `${API}/auth/google/vendor` : `${API}/auth/google/customer`;
                    const r = await axios.post(url, { credential }, { withCredentials: true });
                    if (r.data.needs_profile) {
                      toast({ title: 'Complete your vendor profile', description: 'We need a few business details to register you as a vendor.' });
                      navigate('/vendor/register');
                      return;
                    }
                    persistSession(r.data);
                    toast({ title: 'Welcome!', description: r.data.user?.email || r.data.vendor?.name || '' });
                    navigate(role === 'vendor' ? '/vendor/dashboard' : '/account');
                  } catch (err) { toast({ title: 'Google sign-in failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' }); }
                  finally { setSubmitting(false); }
                }}
              />

              <div className="text-xs text-center text-gray-500 dark:text-gray-400 pt-2">
                {role === 'vendor' ? (
                  <>Not a vendor yet? <Link to="/register?as=vendor" className="font-medium hover:underline" style={{ color: accentColor }} data-testid="hub-link-vendor-register">Register as Vendor</Link></>
                ) : (
                  <>New here? <Link to="/register?as=customer" className="font-medium hover:underline" style={{ color: accentColor }} data-testid="hub-link-customer-register">Register as Customer</Link></>
                )}
              </div>
            </div>
          )}

          {mode === 'otp' && (
            <form onSubmit={verifyOtp} className="space-y-3">
              <div>
                <Label className="text-xs text-gray-700 dark:text-gray-300">Enter OTP for +91 {mobile}</Label>
                <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="4-digit OTP" className="text-center tracking-widest text-lg" data-testid="hub-otp-input" />
              </div>
              {devOtp && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Dev mode OTP: <b className="font-mono">{devOtp}</b></div>}
              <Button type="submit" disabled={submitting} className="w-full h-11 text-white" style={{ backgroundColor: accentColor }} data-testid="hub-verify-otp-btn">
                <KeyRound className="w-4 h-4 mr-2" /> {submitting ? 'Verifying…' : 'Verify & Sign In'}
              </Button>
              <button type="button" onClick={() => { setMode('home'); setOtp(''); }} className="text-xs hover:underline w-full inline-flex items-center justify-center gap-1" style={{ color: accentColor }}>
                <ArrowLeft className="w-3 h-3" /> Change mobile number
              </button>
            </form>
          )}

          {mode === 'forgot' && (
            <form onSubmit={submitForgot} className="space-y-3" data-testid="hub-forgot-form">
              <div>
                <Label className="text-xs text-gray-700 dark:text-gray-300">OTP sent to +91 {mobile}</Label>
                <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="4-digit OTP" className="text-center tracking-widest text-lg" data-testid="hub-fp-otp" />
              </div>
              {devOtp && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Dev OTP: <b className="font-mono">{devOtp}</b></div>}
              <div>
                <Label className="text-xs text-gray-700 dark:text-gray-300">New Password</Label>
                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 8 chars, letters + numbers" autoComplete="new-password" data-testid="hub-fp-newpass" />
              </div>
              <div>
                <Label className="text-xs text-gray-700 dark:text-gray-300">Confirm Password</Label>
                <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Repeat password" data-testid="hub-fp-confirm" />
              </div>
              <Button type="submit" disabled={submitting} className="w-full h-11 text-white" style={{ backgroundColor: accentColor }} data-testid="hub-fp-submit">
                <ShieldCheck className="w-4 h-4 mr-2" /> {submitting ? 'Resetting…' : 'Reset password & sign in'}
              </Button>
              <button type="button" onClick={() => { setMode('home'); setAuthTab('password'); }} className="text-xs hover:underline w-full inline-flex items-center justify-center gap-1" style={{ color: accentColor }}>
                <ArrowLeft className="w-3 h-3" /> Back to sign in
              </button>
            </form>
          )}

          {mode === 'set-pw' && (
            <div className="space-y-3" data-testid="hub-set-pw">
              <div className="text-sm text-gray-600 bg-blue-50 border border-blue-100 rounded p-3">
                You&apos;re signed in! Want to skip OTP next time? Set a password — or skip and do it later.
              </div>
              <div>
                <Label className="text-xs">New Password</Label>
                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 8 chars, letters + numbers" data-testid="hub-sp-newpass" />
              </div>
              <div>
                <Label className="text-xs">Confirm Password</Label>
                <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Repeat password" data-testid="hub-sp-confirm" />
              </div>
              <div className="flex gap-2">
                <Button onClick={setPwNow} disabled={submitting} className="flex-1 h-11 text-white" style={{ backgroundColor: accentColor }} data-testid="hub-sp-set">
                  {submitting ? 'Setting…' : 'Set password'}
                </Button>
                <Button variant="outline" onClick={() => navigate(role === 'vendor' ? '/vendor/dashboard' : '/account')} data-testid="hub-sp-skip">Skip</Button>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default LoginHub;
